import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import { MyCloseIcon, MyFileDownloadIcon } from "../../Assets/svg/MyIcons";
import logo from "../../Assets/icon.png";
import moment from "moment";

const TripolaTransactionInvoiceModal = ({ open, handleClose, transactionData }) => {
  if (!transactionData) return null;

  // Extract data from Tripola transaction
  const {
    orderId,
    transactionId,
    transactionDate,
    status,
    paymentMode,
    billingCycle,
    amount,
    convertedAmount,
    defaultCurrency,
    convertedCurrency,
    conversationRate, // exchange rate for Tripola
    isUpgraded,
    tenantName,
    // Additional fields you might need to add to your API response:
    tenantEmail,
    tenantPhone,
    tenantAddress,
    tenantCity,
    tenantState,
    tenantCountry,
    tenantZipCode,
    planName,
    planDescription,
    planStartDate,
    planEndDate,
    monthlyPrice,
    yearlyPrice,
    pdfUrl = null,
    // Tripola-specific plan features (you might need to add these)
    maxUsers,
    storageLimit,
    apiCallsLimit,
    supportLevel,
    customDomain,
  } = transactionData;

  // Helper functions
  const formatCurrency = (value, currency) => {
    if (!value) return "N/A";
    return `${currency} ${parseFloat(value).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return moment(date).format("MMMM DD, YYYY");
  };

  const formatLimit = (limit) => {
    if (limit === null || limit === undefined) return "Unlimited";
    if (typeof limit === 'string' && limit.toLowerCase() === 'unlimited') return "Unlimited";
    return limit.toLocaleString();
  };

  const getStatusText = (statusCode) => {
    const statusMap = {
      "pending": "Pending",
      "success": "Success",
      "failed": "Failed",
      "refunded": "Refunded",
      // Also support numeric values
      "0": "Pending",
      "1": "Success",
      "2": "Failed",
      "3": "Refunded",
    };
    return statusMap[statusCode] || "Unknown";
  };

  const getStatusClass = (statusCode) => {
    const statusClassMap = {
      "pending": "invoice-modal__status-warning",
      "success": "invoice-modal__status-success",
      "failed": "invoice-modal__status-danger",
      "refunded": "invoice-modal__status-default",
      // Also support numeric values
      "0": "invoice-modal__status-warning",
      "1": "invoice-modal__status-success",
      "2": "invoice-modal__status-danger",
      "3": "invoice-modal__status-default",
    };
    return statusClassMap[statusCode] || "invoice-modal__status-default";
  };

  const getPaymentModeClass = (mode) => {
    return mode === "online"
      ? "invoice-modal__payment-online"
      : "invoice-modal__payment-offline";
  };

  // Calculate plan price based on billing cycle
  const planPrice = billingCycle === "monthly" ? monthlyPrice : yearlyPrice;

  // Calculate converted plan price
  const convertedPlanPrice = planPrice && conversationRate
    ? (parseFloat(planPrice) * parseFloat(conversationRate)).toFixed(2)
    : null;

  // Calculate credit adjustment for upgrades
  const calculateCredit = () => {
    if (!isUpgraded || !planPrice) return 0;
    const credit = parseFloat(planPrice) - parseFloat(amount);
    return credit > 0 ? credit : 0;
  };

  const unusedCredit = calculateCredit();
  const convertedUnusedCredit = conversationRate
    ? (unusedCredit * parseFloat(conversationRate)).toFixed(2)
    : unusedCredit;

  // Calculate remaining days
  const calculateRemainingDays = () => {
    if (!planStartDate || !planEndDate) return 0;
    const today = moment();
    const start = moment(planStartDate);
    const end = moment(planEndDate);

    if (today.isAfter(end, "day")) return 0;

    const totalDays = end.diff(start, "days") + 1;
    const usedDays = today.diff(start, "days") + 1;
    const remainingDays = Math.max(totalDays - usedDays, 0);

    return remainingDays;
  };

  const remainingDays = calculateRemainingDays();

  const isRefund = status === "refunded" || status === "3";

  const handleDownloadPdf = async () => {
    if (!pdfUrl) return;
    try {
      const res = await fetch(pdfUrl);
      const blob = await res.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = res.url.split("/").pop() || "tripola-invoice.pdf";
      a.click();
      window.URL.revokeObjectURL(fileURL);
    } catch (err) {
      console.error("Error downloading PDF:", err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" className="invoice-modal-dialog">
      <DialogTitle className="invoice-modal__header">
        <div className="d-flex justify-content-between align-items-center">
          <Typography variant="h5" className="invoice-modal__header-title">
            {isRefund ? "Tripola Refund Invoice" : "Tripola Transaction Invoice"}
          </Typography>
          <IconButton onClick={handleClose} className="invoice-modal__close-btn">
            <MyCloseIcon className="text-white" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers className="invoice-modal__content">
        {/* Download PDF Button */}
        {pdfUrl && (
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="contained"
              onClick={handleDownloadPdf}
              className="invoice-modal__download-btn"
              startIcon={<MyFileDownloadIcon className="text-white" />}
            >
              Download PDF
            </Button>
          </div>
        )}

        {/* Invoice Container with Scroll */}
        <div className="invoice-modal__scroll-wrapper">
          <div className="invoice-modal__invoice-card">
            <div className="invoice-modal__container">
              {/* Header with Logo and Tenant Info */}
              <div className="invoice-modal__top-header">
                <div className="invoice-modal__header-left">
                  <img src={logo} alt="Tripola Logo" className="invoice-modal__logo" />
                  <Typography className="invoice-modal__title">
                    {isRefund ? "TRIPOLA REFUND INVOICE" : "TRIPOLA INVOICE"}
                  </Typography>
                  <Typography className="invoice-modal__order-id">{orderId ? `#${orderId}` : "N/A"}</Typography>
                </div>
                <div className="invoice-modal__header-right">
                  <Typography className="invoice-modal__company-name">{tenantName || "N/A"}</Typography>
                  {/* <div className="invoice-modal__company-info">
                    <Typography>{tenantAddress || "N/A"}</Typography>
                    <Typography>
                      {tenantCity || "N/A"}, {tenantState || "N/A"}, {tenantCountry || "N/A"} - {tenantZipCode || "N/A"}
                    </Typography>
                    <Typography className="invoice-modal__company-email">{tenantEmail || "N/A"}</Typography>
                    <Typography>{tenantPhone || "N/A"}</Typography>
                  </div> */}
                </div>
              </div>

              {/* Invoice Details Grid */}
              <div className="invoice-modal__details-grid">
                <div className="invoice-modal__detail-item">
                  <Typography className="invoice-modal__detail-label">Invoice Date</Typography>
                  <Typography className="invoice-modal__detail-value">{formatDate(transactionDate)}</Typography>
                </div>
                <div className="invoice-modal__detail-item">
                  <Typography className="invoice-modal__detail-label">Transaction ID</Typography>
                  <Typography className="invoice-modal__detail-value invoice-modal__transaction-id">
                    {transactionId || "N/A"}
                  </Typography>
                </div>
                <div className="invoice-modal__detail-item">
                  <Typography className="invoice-modal__detail-label">Payment Status</Typography>
                  <Typography className={`invoice-modal__detail-value ${getStatusClass(status)}`}>
                    {getStatusText(status)}
                  </Typography>
                </div>
                <div className="invoice-modal__detail-item">
                  <Typography className="invoice-modal__detail-label">Payment Method</Typography>
                  <Typography className={`invoice-modal__detail-value ${getPaymentModeClass(paymentMode)}`}>
                    {paymentMode === "online" ? "Online" : "Offline"}
                  </Typography>
                </div>
              </div>

              {/* Invoice Items Table */}
              <div className="invoice-modal__items-section">
                <div className="invoice-modal__table-wrapper">
                  <div className="invoice-modal__table-header">
                    <div className="invoice-modal__table-header-row">
                      <div className="invoice-modal__col-description">
                        <Typography>Description</Typography>
                      </div>
                      <div className="invoice-modal__col-cycle">
                        <Typography>Billing Cycle</Typography>
                      </div>
                      <div className="invoice-modal__col-price">
                        <Typography>Unit Price</Typography>
                      </div>
                      <div className="invoice-modal__col-amount">
                        <Typography>Amount</Typography>
                      </div>
                    </div>
                  </div>
                  <div className="invoice-modal__table-body">
                    <div className="invoice-modal__table-row">
                      <div className="invoice-modal__col-description">
                        <Typography className="invoice-modal__plan-name">
                          {isRefund ? `Refund - ${planName || "Tripola Plan"}` : planName || "Tripola Plan"}
                        </Typography>
                        <Typography className="invoice-modal__plan-description">{planDescription || "Tripola subscription plan"}</Typography>
                      </div>
                      <div className="invoice-modal__col-cycle">
                        <Typography className="invoice-modal__billing-cycle">
                          {billingCycle ? billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1) : "N/A"}
                        </Typography>
                      </div>
                      <div className="invoice-modal__col-price">
                        <div className="invoice-modal__price-wrapper">
                          <Typography className="invoice-modal__price-main">
                            {convertedPlanPrice
                              ? formatCurrency(convertedPlanPrice, convertedCurrency)
                              : formatCurrency(convertedAmount, convertedCurrency)}
                          </Typography>
                          {convertedCurrency !== defaultCurrency && (
                            <Typography className="invoice-modal__price-secondary">
                              {planPrice ? formatCurrency(planPrice, defaultCurrency) : formatCurrency(amount, defaultCurrency)}
                            </Typography>
                          )}
                        </div>
                      </div>
                      <div className="invoice-modal__col-amount">
                        <div className="invoice-modal__price-wrapper">
                          <Typography className="invoice-modal__amount-main">
                            {convertedPlanPrice
                              ? formatCurrency(convertedPlanPrice, convertedCurrency)
                              : formatCurrency(convertedAmount, convertedCurrency)}
                          </Typography>
                          {convertedCurrency !== defaultCurrency && (
                            <Typography className="invoice-modal__amount-secondary">
                              {planPrice ? formatCurrency(planPrice, defaultCurrency) : formatCurrency(amount, defaultCurrency)}
                            </Typography>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Credit Adjustment Details */}
                    {isUpgraded && unusedCredit > 0 && (
                      <div className="invoice-modal__credit-section">
                        <Typography className="invoice-modal__credit-title">Credit Adjustment Details</Typography>
                        <div className="invoice-modal__credit-row-detail">
                          <div className="invoice-modal__credit-left">
                            <span className="invoice-modal__bullet"></span>
                            <div>
                              <Typography>Previous Plan Unused Credit Adjustment</Typography>
                              {remainingDays > 0 && (
                                <Typography className="invoice-modal__remaining-days">
                                  ({remainingDays} {remainingDays === 1 ? "day" : "days"} remaining)
                                </Typography>
                              )}
                            </div>
                          </div>
                          <div className="invoice-modal__credit-right">
                            <Typography className="invoice-modal__credit-amount">
                              -{formatCurrency(convertedUnusedCredit, convertedCurrency)}
                            </Typography>
                            {convertedCurrency !== defaultCurrency && (
                              <Typography className="invoice-modal__credit-amount-secondary">
                                -{formatCurrency(unusedCredit, defaultCurrency)}
                              </Typography>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tripola Plan Features */}
                    <div className="invoice-modal__features-section">
                      {/* <Typography className="invoice-modal__features-title">Tripola Plan Features</Typography> */}
                      <div className="invoice-modal__features-grid">
                        {/* <div className="invoice-modal__feature-card invoice-modal__feature-card-blue">
                          <Typography className="invoice-modal__feature-label">Max Users</Typography>
                          <Typography className="invoice-modal__feature-value">{formatLimit(maxUsers)}</Typography>
                        </div>
                        <div className="invoice-modal__feature-card invoice-modal__feature-card-green">
                          <Typography className="invoice-modal__feature-label">Storage</Typography>
                          <Typography className="invoice-modal__feature-value">
                            {formatLimit(storageLimit)} {storageLimit && storageLimit !== 'Unlimited' ? 'GB' : ''}
                          </Typography>
                        </div>
                        <div className="invoice-modal__feature-card invoice-modal__feature-card-purple">
                          <Typography className="invoice-modal__feature-label">API Calls</Typography>
                          <Typography className="invoice-modal__feature-value">
                            {formatLimit(apiCallsLimit)} {apiCallsLimit && apiCallsLimit !== 'Unlimited' ? '/month' : ''}
                          </Typography>
                        </div> */}
                        {supportLevel && (
                          <div className="invoice-modal__feature-card invoice-modal__feature-card-orange">
                            <Typography className="invoice-modal__feature-label">Support</Typography>
                            <Typography className="invoice-modal__feature-value">{supportLevel}</Typography>
                          </div>
                        )}
                        {customDomain !== undefined && (
                          <div className="invoice-modal__feature-card invoice-modal__feature-card-teal">
                            <Typography className="invoice-modal__feature-label">Custom Domain</Typography>
                            <Typography className="invoice-modal__feature-value">{customDomain ? 'Included' : 'Not Included'}</Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Totals Section */}
              <div className="invoice-modal__totals-wrapper">
                <div className="invoice-modal__totals">
                  {/* Subtotal - show for (!isRefund || isUpgraded) */}
                  {(!isRefund || isUpgraded) && (
                    <div className="invoice-modal__total-row">
                      <Typography className="invoice-modal__total-label">Subtotal:</Typography>
                      <div className="invoice-modal__total-value-wrapper">
                        <Typography className="invoice-modal__total-value">
                          {convertedPlanPrice
                            ? formatCurrency(convertedPlanPrice, convertedCurrency)
                            : formatCurrency(convertedAmount, convertedCurrency)}
                        </Typography>
                        {convertedCurrency !== defaultCurrency && (
                          <Typography className="invoice-modal__total-value-secondary">
                            {planPrice ? formatCurrency(planPrice, defaultCurrency) : formatCurrency(amount, defaultCurrency)}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Unused Credit */}
                  {isUpgraded && unusedCredit > 0 && (
                    <div className="invoice-modal__credit-row">
                      <div>
                        <Typography className="invoice-modal__credit-label">Credit Adjustment:</Typography>
                        {remainingDays > 0 && (
                          <Typography className="invoice-modal__credit-days">
                            ({remainingDays} {remainingDays === 1 ? "day" : "days"} remaining)
                          </Typography>
                        )}
                      </div>
                      <div className="invoice-modal__credit-value-wrapper">
                        <Typography className="invoice-modal__credit-value">
                          -{formatCurrency(convertedUnusedCredit, convertedCurrency)}
                        </Typography>
                        {convertedCurrency !== defaultCurrency && (
                          <Typography className="invoice-modal__credit-value-secondary">
                            -{formatCurrency(unusedCredit, defaultCurrency)}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  {(!isRefund || isUpgraded || unusedCredit > 0) && <div className="invoice-modal__total-divider"></div>}

                  {/* Total Amount */}
                  <div className="invoice-modal__final-total-row">
                    <Typography className="invoice-modal__final-total-label">
                      {isRefund ? "Refund Amount:" : "Total Amount:"}
                    </Typography>
                    <div className="invoice-modal__final-total-value-wrapper">
                      <Typography className="invoice-modal__final-total-value">
                        {formatCurrency(convertedAmount, convertedCurrency)}
                      </Typography>
                      {convertedCurrency !== defaultCurrency && (
                        <Typography className="invoice-modal__final-total-value-secondary">
                          {formatCurrency(amount, defaultCurrency)}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="invoice-modal__footer">
                <Typography className="invoice-modal__footer-title">Thank you for choosing Tripola!</Typography>
                <Typography className="invoice-modal__footer-text">
                  If you have any questions about this invoice, please contact us at{" "}
                  <a href={`mailto:${tenantEmail}`} className="invoice-modal__footer-email">
                    {tenantEmail || "support@tripola.com"}
                  </a>
                </Typography>
                <Typography className="invoice-modal__footer-note">
                  This is a computer-generated invoice and does not require a signature.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TripolaTransactionInvoiceModal;